import { JSXElement, mergeProps, Show } from 'solid-js';
import * as zod from 'zod';
import { validator } from '@felte/validator-zod';
import InputField from '~/components/ui/Forms/InputField.tsx';
import { Localized } from '~/i18n';
import {
  getTranslationKey,
  handleAddRedirectUriError
} from '~/pages/ApiCredentials/utils.tsx';
import { Button } from '~/components/ui/Button';
import Modal from '~/components/ui/Modal';
import { ModalTypes } from '~/components/ui/Modal/Modal.tsx';
import Form, { ServerError } from '~/components/Form';
import { getLocalizedString } from '~/i18n/utils.ts';
import SubmitButton from '~/components/Form/SubmitButton.tsx';
import { useToast } from '~/components/ui/Toast';
import { useIntl } from '@cookbook/solid-intl';
import Alert from '~/components/ui/Alert';
import { getRedirectsTranslationKey } from '../utils.ts';
import {
  getRedirectUriHeader,
  getRedirectUriSubheader,
  RedirectUriType
} from '~/pages/FullStackAuthentication/Redirects/utils.tsx';
import { getKebabCase } from '~/utils/commonUtils.ts';

type AddRedirectUriModalProps = {
  redirectUris: string[] | string;
  open: boolean;
  onClose: () => void;
  onRedirectUriAdded: (
    redirectUri: string,
    uriToReplace: string
  ) => Promise<any>;
  uriToEdit: string;
  redirectUriType?: RedirectUriType;
};

type FormValueErrorType = {
  redirectUri?: string;
};

export default function AddRedirectUriModal(
  props: AddRedirectUriModalProps
): JSXElement {
  const merged = mergeProps({}, props);

  const toaster = useToast();
  const intl = useIntl();

  const schema = zod.object({
    redirectUri: zod
      .string()
      .url(
        getLocalizedString(
          getTranslationKey('validation_message_valid_redirect_uri')
        )
      )
  });

  const getModalTitle = () => {
    return getLocalizedString(
      getRedirectsTranslationKey(
        merged.uriToEdit
          ? 'modal_title_edit_redirect_uri'
          : 'modal_title_add_redirect_uri'
      ),
      {
        intl,
        header: merged.redirectUriType
          ? getRedirectUriHeader(merged.redirectUriType, intl)
          : getLocalizedString(
              getRedirectsTranslationKey('label_redirect_uri'),
              { intl }
            )
      }
    ).replace(/s$/, '');
  };

  return (
    <Modal
      title={getModalTitle()}
      subTitle={getRedirectUriSubheader(merged.redirectUriType!, intl)}
      open={merged.open}
      type={ModalTypes.DIALOG}
    >
      <Form
        extend={validator({ schema })}
        validate={(values) => {
          const errors: FormValueErrorType = {};
          if (
            merged.redirectUriType !== RedirectUriType.INITIATE_LOGIN_URI &&
            Array.isArray(merged.redirectUris) &&
            merged.redirectUris
              .filter((uri) => uri !== merged.uriToEdit)
              .includes(values.redirectUri)
          ) {
            errors.redirectUri = getLocalizedString(
              getRedirectsTranslationKey(
                'validation_message_duplicate_redirect_uri'
              ),
              {
                intl,
                title: getRedirectUriHeader(
                  merged.redirectUriType!,
                  intl
                ).replace(/s$/, '')
              }
            );
          }
          return errors;
        }}
        onSubmit={async (data) => {
          const editMode = !!merged.uriToEdit;
          const response = await merged.onRedirectUriAdded(
            data.redirectUri,
            merged.uriToEdit // will be empty string in case of add
          );
          return {
            response,
            editMode
          };
        }}
        onSuccess={(data) => {
          toaster.toast().success({
            description: getLocalizedString(
              getRedirectsTranslationKey(
                (data as { editMode: boolean })?.editMode
                  ? 'edit_redirect_uri_success'
                  : 'add_redirect_uri_success'
              ),
              {
                intl,
                title: getRedirectUriHeader(merged.redirectUriType!, intl)
                  .toLowerCase()
                  .replace(/s$/, '')
              }
            )
          });
        }}
        errorHandler={handleAddRedirectUriError}
      >
        {({ reset }) => {
          return (
            <>
              <div class={`w-full`}>
                <ServerError>
                  {({ message }) => (
                    <Show when={message}>
                      <Alert variant={'error'}>{message}</Alert>
                    </Show>
                  )}
                </ServerError>
                <InputField
                  name={'redirectUri'}
                  id={`redirect_uri`}
                  onInput={() => {}}
                  placeholder={getLocalizedString(
                    'input_placeholders.api_credential_redirect_uri'
                  )}
                  value={merged.uriToEdit}
                />
              </div>
              <div class={`flex justify-end mt-6`}>
                <Button
                  size={`sm`}
                  variant={`outline`}
                  class={`mr-2`}
                  type={`button`}
                  onClick={() => {
                    reset();
                    merged.onClose();
                  }}
                  data-testid={
                    getKebabCase(merged.redirectUriType!.toLowerCase()) +
                    '-cancel'
                  }
                >
                  <Localized translationKey={`cta_labels.cancel`} />
                </Button>
                <SubmitButton
                  size={`sm`}
                  data-testid={
                    getKebabCase(merged.redirectUriType!.toLowerCase()) +
                    (!merged.uriToEdit ? '-add' : '-update')
                  }
                >
                  <Show
                    when={!merged.uriToEdit}
                    fallback={
                      <Localized translationKey={`cta_labels.update`} />
                    }
                  >
                    <Localized translationKey={`cta_labels.add`} />
                  </Show>
                </SubmitButton>
              </div>
            </>
          );
        }}
      </Form>
    </Modal>
  );
}
